import React from 'react';
import { Progress } from '@/designSystem/Progress';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/designSystem/Table';
import { FACTOR_COLORS, factorWeights } from '../utils/consts';
import { CompetitorMatchingDetails } from '../types';

interface MatchingTableRowProps {
  factor: string;
  competitorValue: string;
  propertyValue: string;
  weight: string | number;
  contribution?: number;
  isExactMatch?: boolean;
  colorKey?: keyof typeof FACTOR_COLORS;
}

// Reusable row component for the matching table
const MatchingTableRow: React.FC<MatchingTableRowProps> = ({
  factor,
  competitorValue,
  propertyValue,
  weight,
  contribution,
  isExactMatch,
  colorKey
}) => {
  // Determine if this is a direct match comparison row or a contribution row
  const isDirectMatchRow = isExactMatch !== undefined;

  return (
    <TableRow>
      <TableCell className="font-medium">{factor}</TableCell>
      <TableCell>{competitorValue}</TableCell>
      <TableCell>{propertyValue}</TableCell>
      <TableCell className="text-center">
        {weight === 'N/A' ? 'N/A' : `${weight}%`}
      </TableCell>
      <TableCell>
        {isDirectMatchRow ? (
          <span className={isExactMatch ? 'text-green-600 font-medium' : 'text-red-500'}>
            {isExactMatch ? 'Exact Match' : 'No Match'}
          </span>
        ) : contribution && colorKey ? (
          <div className="flex items-center gap-2">
            <Progress
              value={contribution}
              className="h-2 w-16 bg-gray-200"
              indicatorClassName={FACTOR_COLORS[colorKey].bg}
            />
            <span>{contribution.toFixed(1)}%</span>
          </div>
        ) : '-'}
      </TableCell>
    </TableRow>
  );
};

// Type for the table row configuration
type TableRowConfig = (
  | {
    factor: string;
    competitorValue: string;
    propertyValue: string;
    weight: string | number;
    contribution: number | undefined;
    colorKey: keyof typeof FACTOR_COLORS;
    isExactMatch?: undefined;
  }
  | {
    factor: string;
    competitorValue: string;
    propertyValue: string;
    weight: string | number;
    contribution?: undefined;
    colorKey?: undefined;
    isExactMatch: boolean | undefined;
  }
);

// Detailed version for the modal
export const MatchingTable: React.FC<{
  matchingDetails: CompetitorMatchingDetails;
  currencySymbol: string;
  distanceKM: number;
}> = ({ matchingDetails, currencySymbol, distanceKM }) => {
  if (!matchingDetails) return null;

  // Define the rows configuration
  const allTableRows: TableRowConfig[] = [
    // Price (ADR)
    {
      factor: 'Price (ADR)',
      competitorValue: matchingDetails.values?.adr
        ? `${currencySymbol}${Math.round(matchingDetails.values.adr)}`
        : '-',
      propertyValue: matchingDetails.target?.adr
        ? `${currencySymbol}${Math.round(matchingDetails.target.adr)}`
        : '-',
      weight: (factorWeights.adr * 100).toFixed(0),
      contribution: matchingDetails.contributions?.adr,
      colorKey: 'adr'
    },
    // Ranking Position
    {
      factor: 'Ranking Position',
      competitorValue: matchingDetails.values?.rank?.toString() || '-',
      propertyValue: matchingDetails.target?.rank?.toString() || '-',
      weight: (factorWeights.rank * 100).toFixed(0),
      contribution: matchingDetails.contributions?.rank,
      colorKey: 'rank'
    },
    // Review Score
    {
      factor: 'Review Score',
      competitorValue: matchingDetails.values?.review_score?.toFixed(1) || '-',
      propertyValue: matchingDetails.target?.review_score?.toFixed(1) || '-',
      weight: (factorWeights.review_score * 100).toFixed(0),
      contribution: matchingDetails.contributions?.review_score,
      colorKey: 'review_score'
    },
    // Review Count
    {
      factor: 'Review Count',
      competitorValue: matchingDetails.values?.review_count?.toString() || '-',
      propertyValue: matchingDetails.target?.review_count?.toString() || '-',
      weight: (factorWeights.review_count * 100).toFixed(0),
      contribution: matchingDetails.contributions?.review_count,
      colorKey: 'review_count'
    },
    // Matching Available Days
    {
      factor: 'Matching Available Days',
      competitorValue: matchingDetails.values?.matching_days?.toString() || '-',
      propertyValue: matchingDetails.target?.total_available_days?.toString() || '-',
      weight: (factorWeights.availability * 100).toFixed(0),
      contribution: matchingDetails.contributions?.availability,
      colorKey: 'availability'
    },
    // Distance
    {
      factor: 'Distance',
      competitorValue: `${distanceKM.toFixed(1)} km`,
      propertyValue: '0 km',
      weight: (factorWeights.distance * 100).toFixed(0),
      contribution: matchingDetails.contributions?.distance,
      colorKey: 'distance'
    },
  ];

  // Separate rows into those with contributions (to be sorted) and those without
  const contributionRows = allTableRows.filter(row => row.contribution !== undefined);
  const nonContributionRows = [
    // Bedrooms (exact match row)
    {
      factor: 'Bedrooms',
      competitorValue: matchingDetails.values?.nb_bedrooms?.toString() || '-',
      propertyValue: matchingDetails.target?.nb_bedrooms?.toString() || '-',
      weight: 'N/A',
      isExactMatch: matchingDetails.matches?.nb_bedrooms
    },
  ];

  // Sort the contribution rows by contribution value (highest first)
  const sortedContributionRows = contributionRows.sort((a, b) => {
    const contributionA = a.contribution || 0;
    const contributionB = b.contribution || 0;
    return contributionB - contributionA;
  });

  // Combine the sorted contribution rows with the non-contribution rows
  const tableRows: TableRowConfig[] = [...nonContributionRows, ...sortedContributionRows];

  return (
    <div className="p-1">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-100">
              <TableHead>Factor</TableHead>
              <TableHead>Competitor</TableHead>
              <TableHead>Your Property</TableHead>
              <TableHead>Weight</TableHead>
              <TableHead>Similarity</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {tableRows.map((row, index) => (
              <MatchingTableRow
                key={index}
                factor={row.factor}
                competitorValue={row.competitorValue}
                propertyValue={row.propertyValue}
                weight={row.weight}
                contribution={row.contribution}
                isExactMatch={row.isExactMatch}
                colorKey={row.colorKey as keyof typeof FACTOR_COLORS}
              />
            ))}
          </TableBody>
          <TableFooter>
            <TableRow className="bg-blue-50 text-black">
              <TableCell colSpan={4} className="text-right font-medium">
                Total Similarity Score:
              </TableCell>
              <TableCell className="font-bold">
                {matchingDetails.competitor_score}%
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </div>

      <div className="mt-3 text-xs text-gray-500">
        <p>Note: A similarity score of 100% would indicate a perfect competitor.
          Factors with higher weights have more potential impact on the overall similarity score.</p>
      </div>
    </div>
  );
};

export default MatchingTable;